/**
 * Organizations (Keycloak realms) this browser has signed in to, most recent
 * first. The signed-in realm lives in sessionStorage, which every new tab
 * starts without; remembering it here lets a plain link (from the docs, an
 * email or a bookmark) sign in without asking for the organization again.
 * Organization names are not secret: they already travel in URLs.
 */
const STORAGE_KEY = 'ariksa.organizations';
const MAX_REMEMBERED = 5;

const read = (): string[] => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(value)
      ? value.filter(item => typeof item === 'string' && item)
      : [];
  } catch {
    return [];
  }
};

const write = (organizations: string[]) => {
  try {
    if (organizations.length)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(organizations));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked (private mode, policy): fall back to asking.
  }
};

export const rememberedOrganizations = (): string[] => read();

export const rememberOrganization = (organization: string) => {
  if (!organization) return;
  write(
    [organization, ...read().filter(item => item !== organization)].slice(
      0,
      MAX_REMEMBERED,
    ),
  );
};

export const forgetOrganization = (organization: string) => {
  write(read().filter(item => item !== organization));
};

/**
 * The organization to sign in to on load: this tab's realm, then the link's
 * ?organization=, then the only remembered one. With several remembered the
 * user picks (empty result shows the organization prompt).
 */
export const initialOrganization = (
  sessionRealm: string | null,
  href: string,
): string => {
  const queryRealm = new URL(href).searchParams.get('organization');
  if (sessionRealm || queryRealm) return (sessionRealm || queryRealm) as string;
  const remembered = read();
  return remembered.length === 1 ? remembered[0] : '';
};
